import React from 'react';
import styled from 'styled-components';
import { SlideComponent, SlideTableStyle } from '../data/slides';
import { pitchTheme, appTheme } from '../styles/theme';
import SlideChartView from './SlideChartView';
import TapePhoto from './TapePhoto';

const Shape = styled.div<{
  $fill?: string;
  $stroke?: string;
  $strokeWidth?: number;
  $radius?: number;
  $opacity?: number;
  $rounded?: boolean;
}>`
  width: 100%;
  height: 100%;
  background: ${({ $fill }) => $fill ?? 'transparent'};
  border: ${({ $strokeWidth, $stroke }) =>
    $strokeWidth ? `${$strokeWidth}px solid ${$stroke ?? 'transparent'}` : 'none'};
  border-radius: ${({ $radius, $rounded }) =>
    $rounded ? '50%' : `${$radius ?? 0}px`};
  opacity: ${({ $opacity }) => $opacity ?? 1};
`;

const LineShape = styled.div<{ $color?: string; $opacity?: number }>`
  width: 100%;
  height: 100%;
  background: ${({ $color }) => $color ?? pitchTheme.text};
  opacity: ${({ $opacity }) => $opacity ?? 1};
  border-radius: 999px;
`;

const TextBlock = styled.p<{ $color?: string; $opacity?: number }>`
  margin: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: ${pitchTheme.fontContent};
  font-size: clamp(0.75rem, 2.5vw, 1rem);
  font-weight: 500;
  line-height: 1.4;
  color: ${({ $color }) => $color ?? pitchTheme.text};
  opacity: ${({ $opacity }) => $opacity ?? 1};
  white-space: pre-wrap;
`;

interface SlideComponentViewProps {
  component: SlideComponent;
}

const MediaLayer = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
`;

const TableLayer = styled.div<{ $opacity?: number }>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  opacity: ${({ $opacity }) => $opacity ?? 1};
`;

const DataTable = styled.table<{ $tableStyle: SlideTableStyle }>`
  width: 100%;
  height: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: ${pitchTheme.fontContent};
  font-size: clamp(0.625rem, 1.6vw, 0.8125rem);
  line-height: 1.3;
`;

const TableHeadCell = styled.th<{ $tableStyle: SlideTableStyle }>`
  padding: 4px 6px;
  text-align: left;
  font-weight: 600;
  background: ${({ $tableStyle }) => $tableStyle.headerFill ?? appTheme.green};
  color: ${({ $tableStyle }) => $tableStyle.headerText ?? '#ffffff'};
  border: 1px solid ${({ $tableStyle }) => $tableStyle.borderColor ?? pitchTheme.border};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TableCell = styled.td<{ $tableStyle: SlideTableStyle }>`
  padding: 4px 6px;
  background: ${({ $tableStyle }) => $tableStyle.cellFill ?? '#ffffff'};
  color: ${({ $tableStyle }) => $tableStyle.cellText ?? pitchTheme.text};
  border: 1px solid ${({ $tableStyle }) => $tableStyle.borderColor ?? pitchTheme.border};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SlideComponentView: React.FC<SlideComponentViewProps> = ({ component }) => {
  const style = component.style ?? {};

  switch (component.type) {
    case 'rectangle':
      return (
        <Shape
          $fill={style.fill}
          $stroke={style.stroke}
          $strokeWidth={style.strokeWidth}
          $radius={style.borderRadius}
          $opacity={style.opacity}
        />
      );
    case 'ellipse':
      return (
        <Shape
          $fill={style.fill}
          $stroke={style.stroke}
          $strokeWidth={style.strokeWidth}
          $rounded
          $opacity={style.opacity}
        />
      );
    case 'line':
      return <LineShape $color={style.fill ?? style.stroke} $opacity={style.opacity} />;
    case 'image':
      return (
        <MediaLayer>
          <TapePhoto
            src={component.imageUrl ?? ''}
            alt={component.imageAlt ?? 'Slide image'}
            borderRadius={style.borderRadius}
            fillFrame
            showTape={false}
          />
        </MediaLayer>
      );
    case 'text':
      return <TextBlock $color={style.fill} $opacity={style.opacity}>{component.text}</TextBlock>;
    case 'table': {
      const table = component.table;
      if (!table) {
        return null;
      }
      const tableStyle = component.tableStyle ?? {};
      return (
        <TableLayer $opacity={style.opacity}>
          <DataTable $tableStyle={tableStyle}>
            <thead>
              <tr>
                {table.headers.map((header, index) => (
                  <TableHeadCell key={`header-${index}`} $tableStyle={tableStyle}>
                    {header}
                  </TableHeadCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {row.map((cell, colIndex) => (
                    <TableCell key={`cell-${rowIndex}-${colIndex}`} $tableStyle={tableStyle}>
                      {cell}
                    </TableCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableLayer>
      );
    }
    case 'chart': {
      const chart = component.chart;
      if (!chart) {
        return null;
      }
      return (
        <SlideChartView
          data={chart}
          chartStyle={component.chartStyle}
          opacity={style.opacity}
        />
      );
    }
    default:
      return null;
  }
};

export default SlideComponentView;
